import React, { useState, useEffect, useMemo } from 'react';

const Icon = ({ d, size = 16, color = 'currentColor', strokeWidth = 2 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
        {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
    </svg>
);

const icons = {
    calendar: 'M19 4H5a2 2 0 00-2 2v14a2 2 0 00 2 2h14a2 2 0 00 2-2V6a2 2 0 00-2-2z M16 2v4 M8 2v4 M3 10h18',
    chevronLeft: 'M15 18l-6-6 6-6',
    chevronRight: 'M9 18l6-6-6-6',
    check: 'M20 6L9 17l-5-5',
    x: 'M18 6L6 18M6 6l12 12',
    user: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2 M12 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8z',
    clock: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
    info: 'M12 8v4M12 16h.01M22 12A10 10 0 1 1 2 12a10 10 0 0 1 20 0z',
    plus: 'M12 5v14M5 12h14',
    save: 'M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z M17 21v-8H7v8 M7 3v5h8',
};

export default function DriverAttendance({ user, operatorDetails }) {
    const formatDateLocal = (d) => {
        if (!d) return null;
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(formatDateLocal(new Date()));
    const [drivers, setDrivers] = useState([]);
    const [attendance, setAttendance] = useState([]);
    const [loading, setLoading] = useState(true);
    const [markingMode, setMarkingMode] = useState(false);
    const [toast, setToast] = useState(null);

    const [absentModal, setAbsentModal] = useState(null);
    const [absentForm, setAbsentForm] = useState({
        status: 'Absent',
        leaveType: 'Sick',
        startDate: '',
        endDate: ''
    });
    const [isSaving, setIsSaving] = useState(false);

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    const fetchDrivers = async () => {
        try {
            const res = await fetch(`/api/drivers/${user.operatorId}`);
            const data = await res.json();
            const generalDrivers = (Array.isArray(data) ? data : []).filter(d => !d.vendorId);
            setDrivers(generalDrivers);
        } catch (err) {
            console.error("Error fetching drivers:", err);
        }
    };

    const fetchAttendance = async () => {
        const monthStr = formatDateLocal(currentDate).substring(0, 7); // YYYY-MM
        try {
            const res = await fetch(`/api/attendance/${user.operatorId}?month=${monthStr}`);
            const data = await res.json();
            setAttendance(Array.isArray(data) ? data : []);
            setLoading(false);
        } catch (err) {
            console.error("Error fetching attendance:", err);
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDrivers();
    }, [user.operatorId]);

    useEffect(() => {
        fetchAttendance();
    }, [user.operatorId, currentDate]);

    const daysInMonth = useMemo(() => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const date = new Date(year, month, 1);
        const days = [];
        // Pad with previous month's days
        const firstDayIndex = date.getDay();
        for (let i = 0; i < firstDayIndex; i++) {
            days.push(null);
        }
        while (date.getMonth() === month) {
            days.push(new Date(date));
            date.setDate(date.getDate() + 1);
        }
        return days;
    }, [currentDate]);

    const stats = useMemo(() => {
        let presentCount = 0;
        let absentCount = 0;
        let leaveCount = 0;

        drivers.forEach(driver => {
            const att = attendance.find(a => a.date === selectedDate && (a.driverId?._id === driver._id || a.driverId === driver._id));
            let status = 'Present';
            
            if (att) {
                status = att.status;
            } else if (driver.leaves && driver.leaves.length > 0) {
                const hasLeave = driver.leaves.some(l => 
                    l.status === 'Approved' && 
                    l.startDate?.split('T')[0] <= selectedDate && 
                    l.endDate?.split('T')[0] >= selectedDate
                );
                if (hasLeave) status = 'On Leave';
            }

            if (status === 'Present') presentCount++;
            else if (status === 'Absent') absentCount++;
            else if (status === 'On Leave' || status === 'Half Day') leaveCount++;
        });

        return {
            total: drivers.length,
            present: presentCount,
            absent: absentCount,
            onLeave: leaveCount,
            notMarked: 0
        };
    }, [attendance, drivers, selectedDate]);

    const handleMonthChange = (offset) => {
        const newDate = new Date(currentDate);
        newDate.setMonth(newDate.getMonth() + offset);
        setCurrentDate(newDate);
    };

    const markAttendance = async (driverId, status) => {
        const current = attendance.find(a => a.date === selectedDate && (a.driverId?._id === driverId || a.driverId === driverId));
        const newStatus = (current && current.status === status) ? 'Present' : status;

        try {
            const res = await fetch('/api/attendance', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    operatorId: user.operatorId,
                    driverId,
                    date: selectedDate,
                    status: newStatus
                })
            });
            if (res.ok) {
                fetchAttendance();
                showToast(`Status updated to ${newStatus}`);
            }
        } catch (err) {
            showToast('Failed to update status', 'error');
        }
    };

    const openAbsentModal = (driver, status) => {
        setAbsentModal(driver);
        setAbsentForm({
            status,
            leaveType: 'Sick',
            startDate: selectedDate,
            endDate: selectedDate
        });
    };

    const handleSaveAbsence = async () => {
        setIsSaving(true);
        try {
            // 1. Update Driver's leaves array
            const newLeave = {
                leaveType: absentForm.leaveType,
                startDate: absentForm.startDate,
                endDate: absentForm.endDate,
                status: 'Approved' // auto-approve
            };
            const updatedLeaves = [...(absentModal.leaves || []), newLeave];
            const payload = { leaves: updatedLeaves };
            if (absentForm.status === 'On Leave') {
                payload.status = 'On Leave';
            }
            
            await fetch(`/api/drivers/${absentModal._id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            // 2. Mark attendance for the date range
            const start = new Date(absentForm.startDate);
            const end = new Date(absentForm.endDate);
            const datesToMark = [];
            for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                datesToMark.push(formatDateLocal(d));
            }

            await Promise.all(datesToMark.map(date => 
                fetch('/api/attendance', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        operatorId: user.operatorId,
                        driverId: absentModal._id,
                        date: date,
                        status: absentForm.status
                    })
                })
            ));

            showToast(`Successfully marked ${absentForm.status}`);
            setAbsentModal(null);
            fetchDrivers();
            fetchAttendance();
        } catch (err) {
            showToast('Failed to save absence', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const bulkMarkAttendance = async (status) => {
        const todayAttendance = attendance.filter(a => a.date === selectedDate);
        const markedDriverIds = new Set(todayAttendance.map(a => a.driverId?._id || a.driverId));
        const driversToMark = drivers.filter(d => !markedDriverIds.has(d._id));
        
        if (driversToMark.length === 0) {
            showToast('All drivers already marked for this day', 'info');
            return;
        }

        try {
            const res = await fetch('/api/attendance/bulk', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    operatorId: user.operatorId,
                    date: selectedDate,
                    attendanceData: driversToMark.map(d => ({ driverId: d._id, status }))
                })
            });
            if (res.ok) {
                fetchAttendance();
                showToast(`Bulk marked as ${status}`);
            }
        } catch (err) {
            showToast('Bulk update failed', 'error');
        }
    };

    const getAttendanceForDate = (date) => {
        if (!date) return null;
        const dateStr = formatDateLocal(date);
        return attendance.filter(a => a.date === dateStr);
    };

    const getSummaryForDate = (dateStr) => {
        let present = 0;
        let absent = 0;
        let onLeave = 0;

        drivers.forEach(driver => {
            const att = attendance.find(a => a.date === dateStr && (a.driverId?._id === driver._id || a.driverId === driver._id));
            let status = 'Not Marked';
            
            if (att) {
                status = att.status;
            } else if (driver.leaves && driver.leaves.length > 0) {
                const hasLeave = driver.leaves.some(l => 
                    l.status === 'Approved' && 
                    l.startDate?.split('T')[0] <= dateStr && 
                    l.endDate?.split('T')[0] >= dateStr
                );
                if (hasLeave) status = 'On Leave';
            }

            if (status === 'Present') present++;
            else if (status === 'Absent') absent++;
            else if (status === 'On Leave' || status === 'Half Day') onLeave++;
        });

        if (present === 0 && absent === 0 && onLeave === 0) return null;
        return { present, absent: absent + onLeave };
    };

    return (
        <div className="attendance-page">
            <style>{`
                .attendance-page { padding: 30px; background: #f8fafc; min-height: 100vh; font-family: 'Inter', sans-serif; }
                .theme-dark .attendance-page { background: #0f172a; }

                .header { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 30px; }
                .title-wrap h1 { font-size: 28px; font-weight: 800; color: #1e293b; margin: 0; }
                .title-wrap p { color: #64748b; margin: 4px 0 0; font-size: 15px; }
                .theme-dark .title-wrap h1 { color: #f1f5f9; }

                .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; margin-bottom: 30px; }
                .stat-card {
                    background: white; border-radius: 20px; padding: 20px; border: 1px solid #e2e8f0;
                    display: flex; align-items: center; gap: 16px; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.03);
                }
                .theme-dark .stat-card { background: #1e293b; border-color: #334155; }
                .stat-card:hover { transform: translateY(-4px); box-shadow: 0 12px 20px -5px rgba(0,0,0,0.1); }
                .stat-icon {
                    width: 52px; height: 52px; border-radius: 14px; display: flex; align-items: center; justify-content: center;
                }
                .stat-info { display: flex; flex-direction: column; }
                .stat-label { font-size: 12px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 4px; }
                .stat-val { font-size: 26px; font-weight: 800; color: #1e293b; line-height: 1; }
                .theme-dark .stat-val { color: #f1f5f9; }

                .main-layout { display: grid; grid-template-columns: 1.2fr 0.8fr; gap: 30px; align-items: start; }
                
                /* Calendar Styling */
                .calendar-container { 
                    background: white; border-radius: 24px; padding: 24px; border: 1px solid #e2e8f0;
                    box-shadow: 0 10px 25px -5px rgba(0,0,0,0.05);
                }
                .theme-dark .calendar-container { background: #1e293b; border-color: #334155; }
                .cal-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; }
                .cal-month-title { font-size: 20px; font-weight: 800; color: #1e293b; display: flex; align-items: center; gap: 10px; }
                .theme-dark .cal-month-title { color: #f1f5f9; }
                .cal-nav { display: flex; gap: 8px; }
                .cal-nav-btn {
                    width: 36px; height: 36px; border-radius: 10px; border: 1px solid #e2e8f0;
                    background: white; color: #64748b; display: flex; align-items: center; justify-content: center;
                    cursor: pointer; transition: all 0.2s;
                }
                .cal-nav-btn:hover { border-color: #00B2A9; color: #00B2A9; background: #f0f7ff; }
                .theme-dark .cal-nav-btn { background: #0f172a; border-color: #334155; }

                .cal-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 10px; }
                .cal-weekday { 
                    text-align: center; font-size: 11px; font-weight: 700; color: #64748b; 
                    text-transform: uppercase; padding: 10px 0; letter-spacing: 0.1em;
                }
                .cal-day {
                    aspect-ratio: 1; border-radius: 14px; border: 1px solid #e2e8f0;
                    display: flex; flex-direction: column; align-items: center; justify-content: center;
                    cursor: pointer; position: relative; transition: all 0.2s; background: #fff;
                }
                .theme-dark .cal-day { background: #1a2233; border-color: #334155; }
                .cal-day:hover { transform: scale(1.05); border-color: #00B2A9; z-index: 10; box-shadow: 0 10px 15px -3px rgba(99, 102, 241, 0.1); }
                .cal-day.empty { cursor: default; border: none; background: transparent; }
                .cal-day.today { border: 2px solid #00B2A9; background: #f0f7ff; }
                .theme-dark .cal-day.today { background: #063A5C; border-color: #818cf8; }
                .cal-day.selected { background: #00B2A9 !important; border-color: #00B2A9; }
                .cal-day.selected .cal-day-num { color: white !important; }
                .cal-day-num { font-size: 16px; font-weight: 700; color: #1e293b; }
                .theme-dark .cal-day-num { color: #f1f5f9; }
                .cal-day-dots { display: flex; gap: 3px; margin-top: 4px; }
                .dot { width: 4px; height: 4px; border-radius: 50%; }
                .dot.present { background: #10b981; }
                .dot.absent { background: #ef4444; }

                /* Details List */
                .details-container {
                    background: white; border-radius: 24px; border: 1px solid #e2e8f0; overflow: hidden;
                    box-shadow: 0 10px 25px -5px rgba(0,0,0,0.05);
                }
                .theme-dark .details-container { background: #1e293b; border-color: #334155; }
                .details-header { padding: 24px; border-bottom: 1px solid #f1f5f9; display: flex; justify-content: space-between; align-items: center; }
                .theme-dark .details-header { border-bottom-color: #334155; }
                .details-title { font-size: 18px; font-weight: 700; color: #1e293b; }
                .theme-dark .details-title { color: #f1f5f9; }
                
                .action-btns { display: flex; gap: 10px; margin-bottom: 20px; padding: 0 24px; }
                .bulk-btn {
                    flex: 1; padding: 10px; border-radius: 12px; font-size: 13px; font-weight: 600;
                    display: flex; align-items: center; justify-content: center; gap: 6px; cursor: pointer;
                    transition: all 0.2s; border: 1px solid transparent;
                }
                .bulk-present { background: #ecfdf5; color: #059669; border-color: #10b981; }
                .bulk-present:hover { background: #10b981; color: white; }
                .bulk-absent { background: #fef2f2; color: #dc2626; border-color: #ef4444; }
                .bulk-absent:hover { background: #ef4444; color: white; }

                .driver-list { max-height: 500px; overflow-y: auto; padding: 0 24px 24px; }
                .driver-row {
                    display: flex; align-items: center; justify-content: space-between;
                    padding: 14px 0; border-bottom: 1px solid #f1f5f9;
                }
                .theme-dark .driver-row { border-bottom-color: #334155; }
                .driver-row:last-child { border-bottom: none; }
                .driver-info-sm { display: flex; align-items: center; gap: 12px; }
                .avatar-sm { 
                    width: 36px; height: 36px; background: #e6f0fa; color: #11427D; border-radius: 50%;
                    display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 14px;
                }
                .driver-name-sm { font-weight: 600; color: #1e293b; font-size: 14px; }
                .theme-dark .driver-name-sm { color: #f1f5f9; }
                .driver-code-sm { font-size: 11px; color: #94a3b8; text-transform: uppercase; }

                .status-selector { display: flex; gap: 6px; }
                .status-btn {
                    width: 32px; height: 32px; border-radius: 8px; border: 1px solid #e2e8f0;
                    display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s;
                }
                .theme-dark .status-btn { background: #0f172a; border-color: #334155; }
                .status-btn.present { color: #10b981; }
                .status-btn.present.active { background: #10b981; color: white; border-color: #10b981; }
                .status-btn.absent { color: #ef4444; }
                .status-btn.absent.active { background: #ef4444; color: white; border-color: #ef4444; }
                .status-btn.leave { color: #f59e0b; }
                .status-btn.leave.active { background: #f59e0b; color: white; border-color: #f59e0b; }

                .toast {
                    position: fixed; top: 24px; right: 24px; padding: 14px 24px; border-radius: 12px;
                    background: #1e293b; color: white; z-index: 1000; box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1);
                    animation: slideIn 0.3s ease-out; font-weight: 600;
                }
                .toast.success { background: #10b981; }
                .toast.error { background: #ef4444; }
                .toast.info { background: #00B2A9; }
                @keyframes slideIn { from { transform: translateY(-20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
            `}</style>

            {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}

            <div className="header">
                <div className="title-wrap">
                    <h1>Attendance Management</h1>
                    <p>Track and manage daily driver availability and presence</p>
                </div>
                <div className="header-actions">
                    {/* Add any header actions if needed */}
                </div>
            </div>

            <div className="stats-grid">
                <div className="stat-card" style={{ borderLeft: '4px solid #00B2A9' }}>
                    <div className="stat-icon" style={{ background: '#eef2ff' }}>
                        <Icon d={icons.user} color="#11427D" size={24} />
                    </div>
                    <div className="stat-info">
                        <span className="stat-label">Total Drivers</span>
                        <span className="stat-val">{stats.total}</span>
                    </div>
                </div>
                <div className="stat-card" style={{ borderLeft: '4px solid #10b981' }}>
                    <div className="stat-icon" style={{ background: '#ecfdf5' }}>
                        <Icon d={icons.check} color="#059669" size={24} />
                    </div>
                    <div className="stat-info">
                        <span className="stat-label">Present (Selected)</span>
                        <span className="stat-val">{stats.present}</span>
                    </div>
                </div>
                <div className="stat-card" style={{ borderLeft: '4px solid #ef4444' }}>
                    <div className="stat-icon" style={{ background: '#fef2f2' }}>
                        <Icon d={icons.x} color="#dc2626" size={24} />
                    </div>
                    <div className="stat-info">
                        <span className="stat-label">Absent (Selected)</span>
                        <span className="stat-val">{stats.absent}</span>
                    </div>
                </div>
                <div className="stat-card" style={{ borderLeft: '4px solid #f59e0b' }}>
                    <div className="stat-icon" style={{ background: '#fffbeb' }}>
                        <Icon d={icons.clock} color="#d97706" size={24} />
                    </div>
                    <div className="stat-info">
                        <span className="stat-label">Pending</span>
                        <span className="stat-val">{stats.notMarked}</span>
                    </div>
                </div>
            </div>

            <div className="main-layout">
                <div className="calendar-container">
                    <div className="cal-header">
                        <div className="cal-month-title">
                            <Icon d={icons.calendar} size={22} color="#00B2A9" />
                            {currentDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
                        </div>
                        <div className="cal-nav">
                            <button className="cal-nav-btn" onClick={() => handleMonthChange(-1)}><Icon d={icons.chevronLeft} size={18} /></button>
                            <button className="cal-nav-btn" onClick={() => setCurrentDate(new Date())}>Today</button>
                            <button className="cal-nav-btn" onClick={() => handleMonthChange(1)}><Icon d={icons.chevronRight} size={18} /></button>
                        </div>
                    </div>
                    <div className="cal-grid">
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                            <div key={d} className="cal-weekday">{d}</div>
                        ))}
                        {daysInMonth.map((date, i) => {
                            if (!date) return <div key={`empty-${i}`} className="cal-day empty" />;
                            const dateStr = formatDateLocal(date);
                            const isToday = dateStr === formatDateLocal(new Date());
                            const isSelected = dateStr === selectedDate;
                            const summary = getSummaryForDate(dateStr);

                            return (
                                <div 
                                    key={dateStr} 
                                    className={`cal-day ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
                                    onClick={() => setSelectedDate(dateStr)}
                                >
                                    <span className="cal-day-num">{date.getDate()}</span>
                                    {summary && (
                                        <div className="cal-day-dots">
                                            {Array.from({ length: summary.present }).slice(0, 3).map((_, idx) => (
                                                <div key={`p-${idx}`} className="dot present" />
                                            ))}
                                            {Array.from({ length: summary.absent }).slice(0, 3).map((_, idx) => (
                                                <div key={`a-${idx}`} className="dot absent" />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="details-container">
                    <div className="details-header">
                        <div className="details-title">
                            {new Date(selectedDate).toLocaleDateString('default', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </div>
                        <div style={{ color: '#94a3b8', fontSize: '13px', fontWeight: 600 }}>
                            {drivers.length} Total Drivers
                        </div>
                    </div>

                    <div className="driver-list">
                        <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: '12px', marginTop: '10px' }}>Driver List</div>
                        {drivers.map(driver => {
                            const att = attendance.find(a => a.date === selectedDate && (a.driverId?._id === driver._id || a.driverId === driver._id));
                            let status = 'Present';
                            if (att) {
                                status = att.status;
                            } else if (driver.leaves && driver.leaves.length > 0) {
                                const hasLeave = driver.leaves.some(l => 
                                    l.status === 'Approved' && 
                                    l.startDate?.split('T')[0] <= selectedDate && 
                                    l.endDate?.split('T')[0] >= selectedDate
                                );
                                if (hasLeave) status = 'On Leave';
                            }
                            return (
                                <div key={driver._id} className="driver-row">
                                    <div className="driver-info-sm">
                                        <div className="avatar-sm" style={status === 'On Leave' ? { background: '#06b6d4', color: 'white' } : {}}>
                                            {driver.name?.charAt(0)}
                                        </div>
                                        <div>
                                            <div className="driver-name-sm">{driver.name}</div>
                                            <div className="driver-code-sm">{driver.driverCode || 'No Code'}</div>
                                            {status === 'On Leave' && (
                                                <div style={{ color: '#0ea5e9', fontSize: '10px', fontWeight: 800, marginTop: '2px', textTransform: 'uppercase' }}>ON LEAVE</div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="status-selector">
                                        <button 
                                            className={`status-btn absent ${status === 'Absent' ? 'active' : ''}`}
                                            title="Mark Absent"
                                            onClick={() => openAbsentModal(driver, 'Absent')}
                                        >
                                            <Icon d={icons.x} size={16} />
                                        </button>
                                        <button 
                                            className={`status-btn leave ${status === 'On Leave' ? 'active' : ''}`}
                                            title="Mark On Leave"
                                            onClick={() => openAbsentModal(driver, 'On Leave')}
                                        >
                                            <Icon d={icons.clock} size={16} />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                        {drivers.length === 0 && (
                            <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                                <Icon d={icons.info} size={24} />
                                <p style={{ marginTop: '10px' }}>No drivers found</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {absentModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setAbsentModal(null)}>
                    <div style={{ background: '#fff', borderRadius: '24px', width: '100%', maxWidth: '440px', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }} onClick={e => e.stopPropagation()}>
                        <div style={{ padding: '24px 32px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>Mark {absentForm.status}</div>
                            <button onClick={() => setAbsentModal(null)} style={{ border: 'none', background: '#f1f5f9', width: '32px', height: '32px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#64748b' }}>
                                <Icon d={icons.x} size={16} />
                            </button>
                        </div>
                        <div style={{ padding: '32px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#e6f0fa', color: '#11427D', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800 }}>
                                    {absentModal.name?.charAt(0)}
                                </div>
                                <div>
                                    <div style={{ fontWeight: 700, color: '#1e293b' }}>{absentModal.name}</div>
                                    <div style={{ fontSize: '12px', color: '#64748b' }}>{absentModal.driverCode || 'No Code'}</div>
                                </div>
                            </div>
                            
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <label style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Reason</label>
                                <select 
                                    style={{ padding: '12px', borderRadius: '12px', border: '1.5px solid #e2e8f0', outline: 'none', fontWeight: 600, color: '#1e293b' }}
                                    value={absentForm.leaveType}
                                    onChange={e => setAbsentForm({ ...absentForm, leaveType: e.target.value })}
                                >
                                    <option value="Sick">Sick Leave</option>
                                    <option value="Personal">Personal Leave</option>
                                    <option value="Vacation">Vacation</option>
                                    <option value="Unexcused">Unexcused Absence</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>

                            <div style={{ display: 'flex', gap: '16px' }}>
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <label style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>From Date</label>
                                    <input 
                                        type="date"
                                        style={{ padding: '12px', borderRadius: '12px', border: '1.5px solid #e2e8f0', outline: 'none', fontWeight: 600, color: '#1e293b' }}
                                        value={absentForm.startDate}
                                        onChange={e => setAbsentForm({ ...absentForm, startDate: e.target.value })}
                                    />
                                </div>
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <label style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>To Date</label>
                                    <input 
                                        type="date"
                                        style={{ padding: '12px', borderRadius: '12px', border: '1.5px solid #e2e8f0', outline: 'none', fontWeight: 600, color: '#1e293b' }}
                                        value={absentForm.endDate}
                                        onChange={e => setAbsentForm({ ...absentForm, endDate: e.target.value })}
                                    />
                                </div>
                            </div>
                        </div>
                        <div style={{ padding: '20px 32px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <button onClick={() => setAbsentModal(null)} style={{ padding: '12px 24px', borderRadius: '12px', border: '1px solid #e2e8f0', background: 'white', fontWeight: 700, color: '#64748b', cursor: 'pointer' }}>Cancel</button>
                            <button 
                                onClick={handleSaveAbsence}
                                disabled={isSaving}
                                style={{ padding: '12px 24px', borderRadius: '12px', border: 'none', background: absentForm.status === 'Absent' ? '#ef4444' : '#f59e0b', fontWeight: 800, color: 'white', cursor: isSaving ? 'not-allowed' : 'pointer', opacity: isSaving ? 0.7 : 1 }}
                            >
                                {isSaving ? 'Saving...' : `Mark ${absentForm.status}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
